import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import {
    selectCurrentProjectId,
    selectEntity,
    selectFeatureWithParentByEntityName,
    selectFormEntity,
    selectIsFormEntityChanged,
} from '@redux/selectors/selectors';
import { isErrorResponse, isRestApiErrorResponse } from '@utils/utils';
import { MetadataPropertyEntity } from '@api/entities/replancity_MetadataProperty';
import { projectsApi } from '@api/projectsApi';
import { BaseEntity, BaseEntityNameLess } from '@api/entities/BaseEntity';
import {
    setEntity,
    setFormEntityChanged,
    setFormEntity,
    setFeatureWithParent,
} from '@redux/entity/entity-reducer';
import { entityApi, EntityView, SaveEntityProps } from '@api/entityApi';
import { useDispatch } from 'react-redux';
import { useToastContext } from '@context/toastContext';
import { useTranslation } from 'react-i18next';
import { groupUpdateApi } from '@api/groupUpdateApi/groupUpdateApi';
import { usePresetsContext } from '@context/presetsContext';
import { FormDataObj, PartiallyOptional } from '@utils/types';
import { SelectedFeatureProperties } from '@redux/showInfo/types';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import { getSessionStorageItem, setSessionStorageItem } from '@utils/storageUtils';
import { EntityMetadata, UseEntityLoaderProps } from '../types';
import { getVisibleEntityMetadata } from '@features/entities/entity-form/utils';
import { FeatureProperties } from '@redux/map/types';

export const useEntityLoader = <T extends BaseEntityNameLess>(
    {
        entityName,
        saveFnArgs,
        presetProperty,
        groupUpdateTaskArgument,
    }: UseEntityLoaderProps = {} as UseEntityLoaderProps
) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<AxiosError | null>(null);
    //TODO ideally local state should be used, but Redux is used by breadcrumbs nowadays
    // const [entity, setEntity] = useState<any>({});
    const entity = useTypedSelector(state => selectEntity(state, entityName));
    const formEntity = useTypedSelector(state => selectFormEntity(state, entityName));
    const featureWithParent = useTypedSelector(state =>
        selectFeatureWithParentByEntityName(state, entityName)
    );
    const [initialEntity, setInitialEntity] = useState<PartiallyOptional<typeof entity, 'id'>>(
        {} as PartiallyOptional<typeof entity, 'id'>
    );
    const isEntityChanged = useTypedSelector(state => selectIsFormEntityChanged(state, entityName));
    const [metadata, setMetadata] = useState<EntityMetadata>();
    // const metadata = useTypedSelector<MetadataPropertyEntity[]>((state) => selectEntityMetadata(state, entityName));
    const projectId = useTypedSelector(selectCurrentProjectId);
    const { selectedPresetId } = usePresetsContext();
    const abortControllerRef = useRef(new AbortController());
    const dispatch = useDispatch();
    const { addToast } = useToastContext();
    const { t } = useTranslation();

    useEffect(() => {
        return () => {
            setError(null);
        };
    }, []);

    const unsavedEntityChanges: boolean = useMemo(
        () =>
            Object.keys(initialEntity).length > 0 &&
            !!formEntity &&
            !isEqual(initialEntity, formEntity),
        [initialEntity, formEntity]
    );

    //TODO this is redundant. Remove useMemo. Update in future
    useEffect(() => {
        dispatch(setFormEntityChanged({ entityName, isChanged: unsavedEntityChanges }));
    }, [entityName, unsavedEntityChanges]);

    const loadMetadata = useCallback(
        async (entityName: string, selectedPresetId: string): Promise<MetadataPropertyEntity[]> => {
            return await projectsApi.fetchEntityMetadata({
                entityName,
                presetId: selectedPresetId,
                abortSignal: abortControllerRef.current.signal,
            });
        },
        []
    );

    const loadEntity = useCallback(
        async ({
            entityName,
            entityId,
            entityView,
        }: {
            entityName: string;
            entityId: string;
            entityView?: EntityView;
        }): Promise<T> => {
            // try {
            const resp = await entityApi.getEntity<T>({
                entityId: entityId,
                entityName,
                ...(entityView
                    ? {
                          params: {
                              view: entityView,
                          },
                      }
                    : {}),
                abortSignal: abortControllerRef.current.signal,
            });

            //TODO call flyTo util? example was useRoadSegmentLoader

            return resp;
        },
        []
    );

    const saveFn = useCallback(
        async ({ entityName, id, data: entity }: SaveEntityProps): Promise<T> => {
            setLoading(true);

            //removed from sessionStorage on user logout
            const sessStorDataStr = getSessionStorageItem('Replan.EntityDataByEntityName');
            const sessStorData = sessStorDataStr ? JSON.parse(sessStorDataStr) : {};
            const { name, geometryJson, ...rest } = entity;
            setSessionStorageItem(
                'Replan.EntityDataByEntityName',
                JSON.stringify({
                    ...(sessStorData ? sessStorData : {}),
                    [entityName]: rest,
                })
            );

            const resp = await entityApi.saveEntity<T>({
                id,
                entityName,
                data: {
                    ...(presetProperty
                        ? {
                              [presetProperty]: {
                                  id: selectedPresetId,
                              },
                          }
                        : {}),
                    ...entity,
                    ...saveFnArgs?.(entity),
                },
                abortSignal: abortControllerRef.current.signal,
            });

            if (featureWithParent) {
                dispatch(
                    setFeatureWithParent({
                        entityName,
                        ...featureWithParent,
                        ...(geometryJson
                            ? {
                                  feature: geometryJson as GeoJSON.Feature<
                                      GeoJSON.Geometry,
                                      FeatureProperties
                                  >,
                              }
                            : {}),
                    })
                );
            }

            if (isErrorResponse(resp)) {
                setError(entity as any);
            }

            setLoading(false);

            return resp;
        },
        [projectId, selectedPresetId, saveFnArgs, featureWithParent]
    );

    //TODO maybe move somewhere
    const groupSaveFn = useCallback(
        async ({
            selectedFeatures,
            data,
        }: {
            selectedFeatures: SelectedFeatureProperties[];
            data: any;
        }) => {
            let resp;
            if (groupUpdateTaskArgument === 'roadInfrastructureUpdateTask') {
                resp = await groupUpdateApi.groupUpdate(
                    {
                        [groupUpdateTaskArgument]: {
                            source: { ...data },
                            targets: selectedFeatures.map(({ id }) => id),
                        } as any,
                    },
                    abortControllerRef.current.signal
                );
            }
            if (groupUpdateTaskArgument === 'transitRouteUpdateTask') {
                //TODO pass PublicTransitLayerEntityName.ROUTES as sourceEntityName?
                const ids = selectedFeatures
                    .filter(({ entityName }) => entityName === PublicTransitLayerEntityName.ROUTES)
                    .map(({ id }) => id);
                const { vehicleType, ...rest } = data;
                resp = await groupUpdateApi.ptRoutesGroupUpdate(
                    {
                        [groupUpdateTaskArgument]: {
                            source: {
                                ...(vehicleType ? { vehicleType: { id: vehicleType } } : {}),
                                ...rest,
                            },
                            targets: ids,
                        } as any,
                    },
                    abortControllerRef.current.signal
                );
            }

            return resp;
        },
        []
    );

    const groupDeleteFn = useCallback(async ({ presetId, ids }) => {
        return await groupUpdateApi.groupManipulate(
            {
                task: {
                    presetId,
                    type: 'DELETE',
                    targets: ids,
                },
            },
            abortControllerRef.current.signal
        );
    }, []);

    const deleteFn = useCallback(
        async (id: string) => {
            setLoading(true);

            const resp = await entityApi.deleteEntity({
                entityId: id,
                entityName,
                abortSignal: abortControllerRef.current.signal,
            });
            if (!isErrorResponse(resp)) {
                addToast(`${t('common.delete-successful')}`, 'success');
            }
            setLoading(false);

            return resp;
        },
        [entityName]
    );

    const loadData = useCallback(
        async ({
            entityName,
            entityId,
            entityView,
        }: {
            entityName: string;
            entityId?: string | null | undefined;
            entityView?: EntityView;
        }): Promise<void> => {
            setLoading(true);

            const [_metaData, entity] = await Promise.all([
                loadMetadata(entityName, selectedPresetId),
                ...(entityId
                    ? [
                          loadEntity({
                              entityName,
                              entityId,
                              entityView,
                          }),
                      ]
                    : []),
            ]);

            if (isErrorResponse(_metaData) || isRestApiErrorResponse(_metaData)) {
                console.error(
                    'Replan. useEntityLoader.loadMetadata error:',
                    JSON.stringify(metadata)
                );
                setError(_metaData as any);

                setLoading(false);
                return;
            }

            if (isErrorResponse(entity) || isRestApiErrorResponse(entity)) {
                console.error('Replan. useEntityLoader.loadEntity error:', entity);
                setError(entity as any);

                setLoading(false);
                return;
            }

            if (_metaData) {
                const entityDataByEntityNameStr = getSessionStorageItem(
                    'Replan.EntityDataByEntityName'
                );
                const entityDataByEntityName = entityDataByEntityNameStr
                    ? JSON.parse(entityDataByEntityNameStr)
                    : {};
                // stored entityDataByEntityName should be used only for new forms
                const _entityDraft = !entityId ? (entityDataByEntityName?.[entityName] ?? {}) : {};
                const { metadata, entityDraft } = getVisibleEntityMetadata(
                    _metaData,
                    _entityDraft,
                    entity
                );

                setMetadata(metadata);
                // dispatch(setEntityMetadata({entityName, metadata: visibleMetadata}));

                setInitialEntity({
                    ...entityDraft,
                } as any);
                dispatch(
                    setFormEntity({
                        entityName,
                        entity: {
                            ...entityDraft,
                        },
                    })
                );
                dispatch(
                    setEntity({
                        entityName,
                        entity: {
                            ...entityDraft,
                            ...(entityId ? entity : {}),
                        },
                    })
                );
            }

            setLoading(false);
        },
        [selectedPresetId, loadMetadata, loadEntity, getVisibleEntityMetadata]
    );

    const changeForm = useCallback(
        (formDataObj: FormDataObj) => {
            dispatch(setFormEntity({ entityName, entity: { ...formDataObj } }));

            if (featureWithParent) {
                const { geometryJson, ...rest } = formDataObj;
                dispatch(
                    setFeatureWithParent({
                        entityName,
                        ...featureWithParent,
                        ...(geometryJson
                            ? {
                                  feature: JSON.parse(geometryJson.toString()),
                              }
                            : {}),
                    })
                );
            }
        },
        [entity, featureWithParent]
    );

    const resetForm = useCallback(() => {
        dispatch(setFormEntity({ entityName, entity: { ...initialEntity } }));
    }, [initialEntity]);

    return {
        loading,
        initialEntity,
        entity: (formEntity as T) ?? {},
        fullEntity: (entity as T) ?? {},
        unsavedEntityChanges: isEntityChanged,
        metadata,
        loadData,
        loadMetadata,
        changeForm,
        resetForm,
        saveFn,
        groupSaveFn,
        groupDeleteFn,
        deleteFn,
        error,
    };
};
